import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  TourSalesModel,
  TourSaleMutationInput,
} from 'api-hooks/tour/tour-sale';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { CustomLabel } from 'components/elements/custom-form-control';
import {
  Container,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import { FormContent } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  TourSaleFormSchema,
  TourSaleFormType,
  TourSaleItemFormType,
  TourSaleItemGuestFormType,
} from './form-type';
import { SalesDetailCard } from './sales-details-card';
import TourSalesSelectInput from './select-input';
import SummaryTourSale from './summary';
import TourParticipantCard from './tour-sale-item/tour-participant-card';
import useGetDefaultValues, {
  addNewGuest,
} from '../hooks/use-get-default-values';
import { FormDivider, OtherContainer } from '../style';
interface Props {
  tourSale?: TourSalesModel;
  onSubmit: (
    input: TourSaleMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<TourSalesModel | undefined>;
  errorRender?: React.ReactNode;
  defaultValues?: TourSaleFormType;
}

export default function TourSaleForm(props: Props) {
  const { t } = useTranslation();
  const { tourSale } = props;
  const { can } = useAuthorization();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    tourSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(TourSaleFormSchema());

  const methods = useForm<TourSaleFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: TourSaleFormType) => {
      try {
        const { tourSale, tempTransactionAt, ...rest } = values;

        const needApproval =
          rest.transactionAt?.getTime() !== tempTransactionAt?.getTime();

        const result: TourSaleMutationInput = {
          ...rest,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    tourSale?.admin.id === employee?.id ||
    tourSale?.salesman?.id === employee?.id ||
    tourSale?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = tourSale && (isContribute || core) && (
    <>
      <SalesActionComponent
        data={tourSale}
        defaultValues={defaultValues}
        type="tour-sales"
      />
    </>
  );

  const { getValues, setValue } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      const guestPreferences = guestPreferencesTransformer(guest);
      const tourSales: TourSaleItemFormType[] = getValues('tourSales').map(
        (ts) => {
          const tourSaleGuests: TourSaleItemGuestFormType[] = [
            ...ts.tourSaleGuests,
            {
              ...addNewGuest(),
              guestName,
              guestId: guest.id,
              guestPreferences,
            },
          ];
          return {
            ...ts,
            tourSaleGuests,
          };
        },
      );
      setValue('tourSales', tourSales);
    },
    [getValues, setValue],
  );
  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const customViewName =
    tourSale && `${t('common:tour_sale')} - ${tourSale.invoiceNumber}`;

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: TourSalesModel) => {
      setValue('parentRefId', value.refId);
      setValue('tourId', value.tourSales?.[0]?.tour?.id || '');
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!tourSale}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormContent>
        {props.errorRender}

        <FormState>
          {({ editable }) => (
            <FormHeader
              data={tourSale}
              noSubmit
              noCancel
              noEdit
              noDelete
              title={
                tourSale ? (
                  <TitleStatus
                    type="sale-default"
                    status={tourSale.status}
                    title={customViewName ?? t('common:tour_sale')}
                    isCompleted={tourSale.isCompleted}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:tour_sale') })
                )
              }
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        <div>
          {tourSale &&
            (!!tourSale.parentSale ||
              !!(tourSale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={tourSale}
                navigation="ToursalesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <TourSalesSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
              />
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('common:group_code')}
                placeholder={t('common:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </SectionContainer>
          <SeparatorVertical $margin={16} />
          <SalesDetailCard />
          <SeparatorVertical $margin={24} />
          <TourParticipantCard parentName="tourSales.0" />
          <SeparatorVertical $margin={24} />
        </div>

        <Container $style={{ height: 'auto' }}>
          <OtherContainer>
            <CustomLabel label={t('common:discount')} />
            <Input
              type="number"
              name="discount"
              placeholder={t('common:discount')}
              textAlign="right"
              clearable={false}
              noMargin
            />
          </OtherContainer>
          <SeparatorVertical $margin={16} />
          <OtherContainer>
            <CustomLabel label={t('common:other')} />
            <Input
              type="number"
              name="otherCost"
              placeholder={t('common:other')}
              textAlign="right"
              clearable={false}
              noMargin
            />
          </OtherContainer>
        </Container>
        <SeparatorVertical $margin={32} />
        <FormDivider />
      </FormContent>
      <SummaryTourSale tourSale={tourSale} />
    </Form>
  );
}
